// src/components/Header.js
import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import logo from "../assets/logo.png";
import { gaEvent } from "../utils/ga";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/service-owners", label: "Owners" },
  { to: "/service-renters", label: "Renters" },
  // "Insights" intentionally routes to the existing /resources page (renamed on-page to "Insights & Resources")
  { to: "/resources", label: "Insights" },
  { to: "/service-community", label: "Community" },
  { to: "/contact", label: "Contact" },
];

const SWITCH_CTA = { to: "/switch-to-xlz", label: "Switch to XLZ" };

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const toggleMenu = () => setIsOpen(!isOpen);
  const headerRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    let rafId = 0;
    let last = -1;

    const setHeaderHeight = () => {
      const h = headerRef.current?.offsetHeight ?? 0;
      if (h === last) return;
      last = h;

      // Defer DOM write to next frame to avoid ResizeObserver feedback loops
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        document.documentElement.style.setProperty("--app-header-h", `${h}px`);
      });
    };

    setHeaderHeight();

    window.addEventListener("resize", setHeaderHeight);
    window.addEventListener("load", setHeaderHeight);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", setHeaderHeight);
      window.removeEventListener("load", setHeaderHeight);
    };
  }, [isOpen]);

  const isSwitchPage = location.pathname === SWITCH_CTA.to;

  // Existing link styling (keeps your current look)
  const navLinkClass = "text-brand-primary font-semibold hover:underline";

  // CTA styling for the Switch link (Option A)
  const switchCtaClass =
    "inline-flex items-center justify-center rounded-full bg-xlzyellow text-xlzteal font-semibold px-5 py-2 shadow-sm hover:bg-yellow-300 transition focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2";

  const switchCtaActive = "ring-2 ring-yellow-400 ring-offset-2";

  // Track only the "Switch to XLZ" CTA (kept visible even when the nav collapses)
  const trackSwitchCtaClick = (device) => {
    gaEvent("select_content", {
      content_type: "cta",
      action: "click",
      cta: "header_switch",
      destination: SWITCH_CTA.to,
      location: "header",
      device,
    });
  };

  return (
    <header
      ref={headerRef}
      className="bg-white shadow-md fixed w-full top-0 left-0 z-50"
    >
      <div className="max-w-6xl mx-auto flex items-center justify-between py-3 px-6 gap-3">
        {/* Logo */}
        <Link to="/" className="flex items-center">
          <img
            src={logo}
            alt="XLZ Real Estate"
            className="h-10 w-auto"
            decoding="async"
            fetchPriority="high"
          />
        </Link>

        {/* Desktop Menu (nav links only). CTA is decoupled to ensure it never disappears on smaller screens. */}
        <nav className="hidden md:flex items-center space-x-6" aria-label="Primary">
          {NAV.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={navLinkClass}
              aria-current={location.pathname === item.to ? "page" : undefined}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        {/* Right-side actions: persistent CTA + hamburger */}
        <div className="flex items-center gap-2 shrink-0">
          {/* Persistent CTA: stays visible on desktop/tablet and also on mobile */}
          <Link
            to={SWITCH_CTA.to}
            onClick={() => trackSwitchCtaClick("header")}
            className={`${switchCtaClass} ${isSwitchPage ? switchCtaActive : ""} px-3 sm:px-5`}
            aria-current={isSwitchPage ? "page" : undefined}
          >
            {SWITCH_CTA.label}
          </Link>

          {/* Mobile Hamburger */}
          <button
            onClick={toggleMenu}
            className="md:hidden flex items-center justify-center p-2 rounded-md text-brand-primary hover:bg-gray-100"
            aria-expanded={isOpen}
            aria-controls="mobile-nav"
            aria-label={isOpen ? "Close menu" : "Open menu"}
          >
            {isOpen ? (
              <span className="text-2xl">&#10005;</span> /* X icon */
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <nav
          id="mobile-nav"
          className="md:hidden bg-white shadow-md border-t"
          aria-hidden={!isOpen}
        >
          <ul className="flex flex-col items-center space-y-4 py-4">
            {/* Optional: include the CTA inside the menu too (some users expect it there). */}
            <li className="pt-2">
              <Link
                to={SWITCH_CTA.to}
                onClick={() => {
                  trackSwitchCtaClick("mobile");
                  setIsOpen(false);
                }}
                className={`${switchCtaClass} px-6 py-3 ${isSwitchPage ? switchCtaActive : ""}`}
                aria-current={isSwitchPage ? "page" : undefined}
              >
                {SWITCH_CTA.label}
              </Link>
            </li>

            {NAV.map((item) => {
              const isActive = location.pathname === item.to;

              return (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    onClick={() => {
                      setIsOpen(false);
                    }}
                    className={navLinkClass}
                    aria-current={isActive ? "page" : undefined}
                  >
                    {item.label}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
      )}
    </header>
  );
}
